import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, Plus } from "lucide-react";
import { api } from "../utils/api";
import { downloadCsv } from "../utils/csv";
import { fmtDate } from "../utils/fmt";
import { useAuth } from "../contexts/AuthContext";
import { SR_TEMPLATES } from "../utils/srTemplates";
import AttachmentUpload from "../components/AttachmentUpload";
import FilterBar, { matchFilters } from "../components/FilterBar";
import StatusSegments from "../components/StatusSegments";

const CATEGORIES = ["SAP", "Other Applications", "Hardware Support", "Software Support", "Network & Infrastructure", "IT Security", "Access Management"];
const PLANTS = ["TGS", "KASESE", "NAMANVE"];
const STATUSES = ["pending", "it_security_review", "it_manager_review", "approved", "rejected", "assigned", "in_progress", "pending_user_info", "pending_vendor_info", "completed", "closed", "reopened", "canceled"];

export default function ServiceRequests() {
  const { user, hasRole } = useAuth();
  const isIT = hasRole("admin", "it_team", "it_manager", "it_security");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [segment, setSegment] = useState("open"); // v2.0 #9
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: CATEGORIES[0], plant: "TGS", request_type: "general", attachments: [], requester_id_override: "" });
  const [tplIdx, setTplIdx] = useState("");

  async function load() { const { data } = await api.get("/api/service-requests"); setItems(data); }
  useEffect(() => {
    load();
    api.get("/api/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  function applyTemplate(idx) {
    setTplIdx(idx);
    if (idx === "") return;
    const t = SR_TEMPLATES[Number(idx)];
    if (!t) return;
    setForm((f) => ({ ...f, title: t.title, description: t.description, category: t.category, request_type: t.request_type }));
  }

  async function create(e) {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.requester_id_override) delete payload.requester_id_override;
    await api.post("/api/service-requests", payload);
    setShow(false);
    setForm({ title: "", description: "", category: CATEGORIES[0], plant: "TGS", request_type: "general", attachments: [], requester_id_override: "" });
    setTplIdx("");
    load();
  }

  function exportCsv() {
    downloadCsv(`service-requests-${new Date().toISOString().slice(0, 10)}.csv`, filtered, [
      "request_number", "request_type", "title", "category", "plant", "status",
      "requester_name", "assigned_to_name", "it_security_approved", "it_manager_approved",
      "is_overdue", "created_at", "closed_at",
    ]);
  }

  const filtered = items.filter((it) => {
    // v2.0 #9
    if (segment === "open" && ["completed", "closed", "canceled", "rejected"].includes(it.status)) return false;
    if (segment === "completed" && it.status !== "completed") return false;
    if (segment === "closed" && it.status !== "closed") return false;
    if (segment === "canceled" && !["canceled", "rejected"].includes(it.status)) return false;
    if (!matchFilters(it, filters)) return false;
    if (search) {
      const hay = `${it.request_number} ${it.title} ${it.requester_name || ""} ${it.assigned_to_name || ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const filterOptions = [
    { key: "status", label: "Status", values: STATUSES },
    { key: "request_type", label: "Type", values: ["access", "general"] },
    { key: "plant", label: "Plant", values: PLANTS },
    { key: "category", label: "Category", values: CATEGORIES },
    { key: "is_overdue", label: "Overdue", values: [{ value: "yes", label: "Overdue only" }, { value: "no", label: "Not overdue" }] },
    { key: "assignee", label: "Assigned to", values: users.filter((u) => ["it_team", "it_manager", "it_security"].includes(u.role)).map((u) => ({ value: u.id, label: u.full_name })) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Service Requests <span className="text-sm font-normal text-gray-500">({filtered.length}{filtered.length !== items.length && `/${items.length}`})</span></h1>
        <div className="flex gap-2">
          <button className="btn-secondary flex items-center gap-2" onClick={exportCsv}><Download size={16} /> CSV</button>
          <button className="btn-primary flex items-center gap-2" onClick={() => setShow(true)}>
            <Plus size={16} /> New Request
          </button>
        </div>
      </div>

      <StatusSegments
        value={segment} onChange={setSegment} items={items}
        segments={[
          { key: "open",      label: "Open",      match: (x) => !["completed","closed","canceled","rejected"].includes(x.status) },
          { key: "completed", label: "Completed", match: (x) => x.status === "completed" },
          { key: "closed",    label: "Closed",    match: (x) => x.status === "closed" },
          { key: "canceled",  label: "Cancelled / Rejected", match: (x) => ["canceled","rejected"].includes(x.status) },
          { key: "all",       label: "All",       match: () => true },
        ]}
      />

      <FilterBar
        filters={filters} onChange={setFilters}
        options={filterOptions}
        onClear={() => setFilters({})}
        search={search} onSearchChange={setSearch}
        searchPlaceholder="Search by title, number, requester, assignee…"
      />

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="p-3">Number</th>
              <th className="p-3">Title</th>
              <th className="p-3">Caller</th>
              <th className="p-3">Type</th>
              <th className="p-3">Category</th>
              <th className="p-3">Plant</th>
              <th className="p-3">Status</th>
              <th className="p-3">Assigned To</th>
              <th className="p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t hover:bg-gray-50">
                <td className="p-3 font-mono text-xs"><Link to={`/service-requests/${it.id}`} className="text-brand-600 hover:underline">{it.request_number}</Link></td>
                <td className="p-3">{it.title}{it.is_overdue && <span className="ml-2 badge bg-red-100 text-red-700">OVERDUE</span>}</td>
                <td className="p-3 text-gray-700">{it.requester_name || "—"}</td>
                <td className="p-3"><span className={`badge ${it.request_type === "access" ? "bg-violet-100 text-violet-700 ring-1 ring-violet-200" : "bg-sky-100 text-sky-700 ring-1 ring-sky-200"}`}>{it.request_type}</span></td>
                <td className="p-3">{it.category}</td>
                <td className="p-3">{it.plant}</td>
                <td className="p-3"><span className={`badge status-${it.status}`}>{it.status.replace(/_/g, " ")}</span></td>
                <td className="p-3">{it.assigned_to_name || "—"}</td>
                <td className="p-3 text-gray-500 text-xs">{fmtDate(it.created_at)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-gray-500">No service requests match the current filters</td></tr>}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <form onSubmit={create} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-brand-500 text-white px-5 py-3 rounded-t-lg font-semibold">New Service Request</div>
            <div className="p-5 space-y-3">
              <div>
                <label className="label flex items-center gap-1"><FileText size={14} /> Start from template <span className="text-xs text-gray-400 font-normal">(optional)</span></label>
                <select className="input" value={tplIdx} onChange={(e) => applyTemplate(e.target.value)}>
                  <option value="">— Blank —</option>
                  {SR_TEMPLATES.map((t, i) => <option key={i} value={i}>{t.name}</option>)}
                </select>
              </div>
              <div><label className="label">Type</label>
                <select className="input" value={form.request_type} onChange={(e) => setForm({ ...form, request_type: e.target.value })}>
                  <option value="general">General</option>
                  <option value="access">Access (requires IT Security + IT Manager approval)</option>
                </select></div>
              <div><label className="label">Title</label><input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><label className="label">Description</label><textarea className="input" rows="6" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              {isIT && (
                <div>
                  <label className="label">
                    Caller / Requester <span className="text-xs text-gray-400 font-normal">(if submitting on behalf of someone else)</span>
                  </label>
                  <select className="input" value={form.requester_id_override}
                    onChange={(e) => setForm({ ...form, requester_id_override: e.target.value })}>
                    <option value="">— Me ({user?.full_name}) —</option>
                    {users.filter((u) => u.is_active !== false && u.id !== user?.id).map((u) =>
                      <option key={u.id} value={u.id}>{u.full_name} ({u.role}{u.plant ? ` · ${u.plant}` : ""})</option>
                    )}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Category</label><select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
                <div><label className="label">Plant</label><select className="input" value={form.plant} onChange={(e) => setForm({ ...form, plant: e.target.value })}>{PLANTS.map((p) => <option key={p}>{p}</option>)}</select></div>
              </div>
              <AttachmentUpload value={form.attachments} onChange={(a) => setForm({ ...form, attachments: a })} />
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 bg-gray-50 rounded-b-lg">
              <button type="button" className="btn-secondary" onClick={() => setShow(false)}>Cancel</button>
              <button className="btn-primary">Submit</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
